import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  CircularProgress, TextField, MenuItem, Button, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Divider, Tooltip, IconButton,
} from '@mui/material';
import { Add, Edit, AttachFile, OpenInNew, ReceiptLong } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

// Super-admin only: what BT actually invoiced per trunk vs what our system assumed
// (channel rental from the carrier record + rate-card buy cost of the month's minutes).

const usd = (v: any) => (v === null || v === undefined) ? '—'
  : (Number(v) < 0 ? '-$' : '$') + Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rate = (v: any) => (v === null || v === undefined) ? '—' : '$' + Number(v).toFixed(4);
const int = (v: any) => Number(v || 0).toLocaleString('en-US');
const pct = (v: any) => (v === null || v === undefined) ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
// Difference is actual - assumed: negative means BT billed less than we assumed (good for us).
const diffColor = (v: any) => (v === null || v === undefined || Math.abs(Number(v)) < 0.005) ? 'text.secondary' : Number(v) < 0 ? 'success.main' : 'error.main';
const profitColor = (v: any) => Number(v) < 0 ? 'error.main' : 'success.main';

const prevMonth = () => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };
const PREFIXES: Record<string, string> = { 'BT UK': '44', 'BT US': '1' };

const Stat: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ flex: '1 1 180px' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

export const BtBillReconPage: React.FC = () => {
  const role = useAuthStore(s => s.user?.role);
  const { enqueueSnackbar } = useSnackbar();
  const [month, setMonth] = useState('');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);
  const [newPlan, setNewPlan] = useState<any>({ carrier_id: '', effective_from: '', channels: '', monthly_fee: '', note: '' });
  const loadPlans = useCallback(async () => {
    try { const r = await api.get('/carrier-bills/channel-plans'); setPlans(r.data || []); } catch { /* shown elsewhere */ }
  }, []);
  useEffect(() => { if (role === 'super_admin') loadPlans(); }, [role, loadPlans]);
  const addPlan = async () => {
    try {
      await api.post('/carrier-bills/channel-plans', newPlan);
      enqueueSnackbar('Channel plan saved', { variant: 'success' });
      setNewPlan({ carrier_id: newPlan.carrier_id, effective_from: '', channels: '', monthly_fee: '', note: '' });
      loadPlans(); load(month);
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed to save plan', { variant: 'error' }); }
  };
  const delPlan = async (id: number) => {
    if (!window.confirm('Delete this channel plan entry?')) return;
    await api.delete(`/carrier-bills/channel-plans/${id}`); loadPlans(); load(month);
  };

  const load = useCallback(async (m: string) => {
    setLoading(true);
    try {
      const r = await api.get('/carrier-bills/reconciliation', { params: m ? { month: m } : {} });
      setData(r.data);
      if (!m) setMonth(r.data.month);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Failed to load', { variant: 'error' });
    } finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { if (role === 'super_admin') load(''); }, [role, load]);

  if (role !== 'super_admin') return <Alert severity="warning">This page is available to super admins only.</Alert>;

  const openAttachment = async (id: string) => {
    try {
      const r = await api.get(`/carrier-bills/${id}/attachment`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { enqueueSnackbar('No attachment for this bill', { variant: 'warning' }); }
  };

  const newBill = (t: any) => {
    const m = data?.month || prevMonth();
    setFile(null);
    setEdit({
      carrier_id: t.carrier_id, carrier_label: t.label, supplier: t.bills[0]?.supplier || 'BT Solutions Limited (Bahrain Branch)',
      invoice_number: '', customer_number: t.bills[0]?.customer_number || '', issue_date: '',
      period_start: data?.period_start || `${m}-01`, period_end: data?.period_end || '',
      channels_qty: t.assumed.channels, channel_rate: t.assumed.channel_rate,
      usage_amount: '', other_amount: '', discount_amount: '', notes: '',
    });
  };
  const editBill = (t: any, b: any) => { setFile(null); setEdit({ ...b, carrier_id: t.carrier_id, carrier_label: t.label }); };

  const recurring = edit ? Math.round(Number(edit.channels_qty || 0) * Number(edit.channel_rate || 0) * 100) / 100 : 0;
  const total = edit ? Math.round((recurring + Number(edit.usage_amount || 0) + Number(edit.other_amount || 0) - Number(edit.discount_amount || 0)) * 100) / 100 : 0;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...edit, recurring_amount: recurring, total_amount: total,
        dst_prefixes: PREFIXES[edit.carrier_label] ? [PREFIXES[edit.carrier_label]] : [],
      };
      const r = edit.id ? await api.put(`/carrier-bills/${edit.id}`, body) : await api.post('/carrier-bills', body);
      if (file) {
        const fd = new FormData(); fd.append('file', file);
        await api.post(`/carrier-bills/${r.data.id}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      enqueueSnackbar('Bill saved', { variant: 'success' });
      setEdit(null);
      const billMonth = String(edit.period_start).slice(0, 7);
      setMonth(billMonth); load(billMonth);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const f = (k: string) => ({
    value: edit?.[k] ?? '', size: 'small' as const, fullWidth: true,
    onChange: (e: any) => setEdit({ ...edit, [k]: e.target.value }),
  });

  const t = data?.totals;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>BT Bill vs Assumed</Typography>
          <Typography variant="body2" color="text.secondary">
            What BT actually invoiced, per trunk, against what our system assumed for the same month.
          </Typography>
        </Box>
        <TextField
          type="month" size="small" label="Month" value={month} InputLabelProps={{ shrink: true }}
          onChange={e => { setMonth(e.target.value); if (e.target.value) load(e.target.value); }}
        />
        {data?.bill_months?.length > 0 && (
          <TextField select size="small" label="Billed months" value="" sx={{ minWidth: 150 }}
            onChange={e => { setMonth(e.target.value); load(e.target.value); }}>
            {data.bill_months.map((m: string) => <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>)}
          </TextField>
        )}
      </Stack>

      {loading && !data && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

      {data && (
        <>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2, opacity: loading ? 0.5 : 1 }}>
            <Stat label={`Revenue · ${monthLabel(data.month)}`} value={usd(t.revenue)} sub="both BT trunks, outbound" />
            <Stat label="Assumed cost" value={usd(t.assumed)} sub="channels + rate-card usage" />
            <Stat label="BT actual billed" value={t.trunks_billed ? usd(t.actual_billed) : '—'}
              sub={`${t.trunks_billed} of ${t.trunks_total} trunks billed`} />
            <Stat label="Difference (actual − assumed)" value={t.trunks_billed ? usd(t.difference) : '—'}
              color={t.trunks_billed ? diffColor(t.difference) : undefined}
              sub={t.trunks_billed ? (t.difference < 0 ? 'BT billed less than assumed' : 'BT billed more than assumed') + ' · billed trunks only' : 'no bills for this month yet'} />
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, opacity: loading ? 0.5 : 1 }}>
            {data.trunks.map((tr: any) => {
              const A = tr.assumed, X = tr.actual, D = tr.difference;
              const row = (label: string, a: any, x: any, d: any, hint?: string, bold = false) => (
                <TableRow>
                  <TableCell sx={{ fontWeight: bold ? 700 : 400 }}>{label}{hint && <Typography variant="caption" color="text.secondary" display="block">{hint}</Typography>}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{usd(a)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{X ? usd(x) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, color: diffColor(d) }}>{X ? usd(d) : '—'}</TableCell>
                </TableRow>
              );
              return (
                <Card key={tr.carrier_id} variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <ReceiptLong color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{tr.label}</Typography>
                      {X ? <Chip size="small" color="success" label="Bill received" />
                        : <Chip size="small" color="warning" label={`Awaiting BT bill`} />}
                      <Button size="small" startIcon={<Add />} onClick={() => newBill(tr)}>Add bill</Button>
                    </Stack>

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell align="right">Assumed</TableCell>
                          <TableCell align="right">BT actual</TableCell>
                          <TableCell align="right">Difference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row('Channel rental', A.recurring, X?.recurring, D?.recurring,
                          `${int(A.channels)} ch × $${Number(A.channel_rate).toFixed(2)}` + (X ? `  ·  billed ${int(X.channels)} ch × $${Number(X.channel_rate).toFixed(2)}` : ''))}
                        {row('Usage', A.usage, X?.usage, D?.usage, `${int(tr.traffic.minutes)} min at rate-card buy rates`)}
                        {X && Math.abs(X.other) >= 0.005 && row('Other / discount', 0, X.other, D?.other)}
                        {row('Total', A.total, X?.total, D?.total, X && D?.total_pct !== null ? `${pct(D.total_pct)} vs assumed` : undefined, true)}
                      </TableBody>
                    </Table>

                    <Divider sx={{ my: 1.5 }} />
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Result</TableCell>
                          <TableCell align="right">On assumed cost</TableCell>
                          <TableCell align="right">On BT actual</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Revenue <Typography variant="caption" color="text.secondary" display="block">{int(tr.traffic.calls)} calls · {int(tr.traffic.answered)} answered · {rate(tr.revenue_per_minute)}/min</Typography></TableCell>
                          <TableCell align="right">{usd(tr.revenue)}</TableCell>
                          <TableCell align="right">{usd(tr.revenue)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Profit</TableCell>
                          <TableCell align="right" sx={{ color: profitColor(tr.assumed_result.profit), fontWeight: 600 }}>{usd(tr.assumed_result.profit)}</TableCell>
                          <TableCell align="right" sx={{ color: tr.actual_result ? profitColor(tr.actual_result.profit) : undefined, fontWeight: 600 }}>{tr.actual_result ? usd(tr.actual_result.profit) : '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Margin</TableCell>
                          <TableCell align="right">{tr.assumed_result.margin_pct === null ? '—' : `${tr.assumed_result.margin_pct}%`}</TableCell>
                          <TableCell align="right">{tr.actual_result?.margin_pct == null ? '—' : `${tr.actual_result.margin_pct}%`}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>All-in cost / min</TableCell>
                          <TableCell align="right">{rate(tr.assumed_result.cost_per_minute)}</TableCell>
                          <TableCell align="right">{tr.actual_result ? rate(tr.actual_result.cost_per_minute) : '—'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {tr.bills.map((b: any) => (
                      <Box key={b.id} sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                            Invoice {b.invoice_number || '—'} · issued {b.issue_date || '—'} · {b.period_start} → {b.period_end} · {usd(b.total_amount)}
                          </Typography>
                          {b.has_attachment && <Tooltip title="View bill"><IconButton size="small" onClick={() => openAttachment(b.id)}><OpenInNew fontSize="small" /></IconButton></Tooltip>}
                          <Tooltip title="Edit bill"><IconButton size="small" onClick={() => editBill(tr, b)}><Edit fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                        {b.notes && <Typography variant="caption" color="text.secondary">{b.notes}</Typography>}
                      </Box>
                    ))}
                    {!X && (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        No BT bill entered for {tr.label} in {monthLabel(data.month)}. Figures above are what we assumed; add the bill when it arrives to see the difference.
                      </Alert>
                    )}

                    {tr.customers.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Traffic by customer</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Customer</TableCell>
                              <TableCell align="right">Minutes</TableCell>
                              <TableCell align="right">Revenue</TableCell>
                              <TableCell align="right">Assumed usage</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tr.customers.map((c: any) => (
                              <TableRow key={c.customer}>
                                <TableCell>{c.customer}</TableCell>
                                <TableCell align="right">{int(c.minutes)}</TableCell>
                                <TableCell align="right">{usd(c.revenue)}</TableCell>
                                <TableCell align="right">{usd(c.usage_cost)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Assumed = channels × monthly fee from the carrier record + rate-card buy cost of the trunk's outbound minutes in the month.
            Difference = BT actual − assumed; green means BT billed less than we assumed. Traffic figures come from the 5-minute CDR rollup.
          </Typography>
        </>
      )}

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{edit?.id ? 'Edit' : 'Add'} BT bill · {edit?.carrier_label}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1 }}>
            <TextField label="Supplier" {...f('supplier')} sx={{ gridColumn: '1 / -1' }} />
            <TextField label="Invoice number" {...f('invoice_number')} />
            <TextField label="Customer number" {...f('customer_number')} />
            <TextField label="Invoice date" type="date" InputLabelProps={{ shrink: true }} {...f('issue_date')} />
            <Box />
            <TextField label="Period start" type="date" InputLabelProps={{ shrink: true }} {...f('period_start')} />
            <TextField label="Period end" type="date" InputLabelProps={{ shrink: true }} {...f('period_end')} />
            <TextField label="Channels" type="number" {...f('channels_qty')} />
            <TextField label="Rate per channel / month (USD)" type="number" {...f('channel_rate')} />
            <TextField label="Usage charges (USD)" type="number" {...f('usage_amount')} />
            <TextField label="Other charges (USD)" type="number" {...f('other_amount')} />
            <TextField label="Discount (USD)" type="number" {...f('discount_amount')} />
            <Box />
            <Alert severity="info" icon={false} sx={{ gridColumn: '1 / -1' }}>
              Recurring {usd(recurring)} · Total of this bill <b>{usd(total)}</b>
            </Alert>
            <TextField label="Notes" multiline minRows={2} {...f('notes')} sx={{ gridColumn: '1 / -1' }} />
            <Button component="label" variant="outlined" startIcon={<AttachFile />} sx={{ gridColumn: '1 / -1' }}>
              {file ? file.name : edit?.has_attachment ? 'Replace bill PDF / image' : 'Attach bill PDF / image'}
              <input hidden type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !edit?.period_start || !edit?.period_end}>
            {saving ? 'Saving…' : 'Save bill'}
          </Button>
        </DialogActions>
      </Dialog>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Channel plans (channels × price per channel per month)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each row applies from its date until the next row. Monthly rental is pro-rated by day when a plan changes mid-month.
            The plan in force today also sets the live channel limit for that trunk.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Trunk</TableCell><TableCell>From</TableCell><TableCell align="right">Channels</TableCell>
                <TableCell align="right">Price / channel</TableCell><TableCell align="right">Monthly</TableCell><TableCell>Note</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {plans.map((p: any) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.carrier}</TableCell><TableCell>{p.effective_from}</TableCell>
                    <TableCell align="right">{int(p.channels)}</TableCell><TableCell align="right">{usd(p.monthly_fee)}</TableCell>
                    <TableCell align="right">{usd(p.monthly_total)}</TableCell><TableCell>{p.note || '—'}</TableCell>
                    <TableCell><Button size="small" color="error" onClick={() => delPlan(p.id)}>Delete</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            <TextField select size="small" label="Trunk" value={newPlan.carrier_id} sx={{ minWidth: 160 }}
              onChange={e => setNewPlan({ ...newPlan, carrier_id: e.target.value })}>
              {Array.from(new Map(plans.map((p: any) => [p.carrier_id, p.carrier])).entries()).map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={newPlan.effective_from}
              onChange={e => setNewPlan({ ...newPlan, effective_from: e.target.value })} />
            <TextField size="small" type="number" label="Channels" value={newPlan.channels} onChange={e => setNewPlan({ ...newPlan, channels: e.target.value })} />
            <TextField size="small" type="number" label="Price / channel" value={newPlan.monthly_fee} onChange={e => setNewPlan({ ...newPlan, monthly_fee: e.target.value })} />
            <TextField size="small" label="Note" value={newPlan.note} onChange={e => setNewPlan({ ...newPlan, note: e.target.value })} sx={{ flexGrow: 1 }} />
            <Button variant="contained" startIcon={<Add />} onClick={addPlan}
              disabled={!newPlan.carrier_id || !newPlan.effective_from || newPlan.channels === '' || newPlan.monthly_fee === ''}>Add plan</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
